import React, { KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { Check, ChevronDown, ChevronRight } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

/**
 * One node of the tree. The shape is deliberately free of any domain: the tree shows
 * a hierarchy of pickable things, it does not know they are accounts.
 */
export interface TreeNode {
    id: string;
    label: ReactNode;
    /** Secondary identifier printed beside the name, in the monospaced face. */
    meta?: ReactNode;
    /**
     * Short qualifiers of the node: its level, its nature, where it comes from.
     *
     * A **list**, because two qualifiers of different natures land on the same row:
     * what the node is *in the tree* (« compte enfant ») and what something outside
     * the tree says about it (« désigné par la source »). Folded into one chip they
     * would read as a single qualifier, and the reader would have no way of telling
     * which half is a fact of the hierarchy and which half is a fact about the person.
     */
    badges?: ReactNode[];
    children?: TreeNode[];
    /**
     * `false` for a node that structures the tree without being a choice: the header
     * of a group whose own row is not offered. It carries no checkbox and no state.
     */
    selectable?: boolean;
    disabled?: boolean;
    /**
     * Why this node cannot be picked. A node that refuses without saying why reads as
     * a broken one, so it is rendered, not only announced.
     */
    disabledReason?: ReactNode;
}

interface Props {
    nodes: TreeNode[];
    /** Ids of the picked nodes. */
    selected: string[];
    onToggle: (id: string, checked: boolean) => void;
    /**
     * Picking a whole branch in one gesture. **Optional, and never automatic**: each
     * node is picked for itself, so that « attached to the master account, and to it
     * alone » stays both expressible and readable. Propagating a parent's tick to its
     * children would make that state impossible to reach and impossible to re-read.
     *
     * Receives the node plus every selectable, enabled descendant that is not picked
     * yet: the caller only has to add them.
     */
    onSelectBranch?: (ids: string[]) => void;
    /** Wording of that gesture. The tree carries no string of its own. */
    branchActionLabel?: ReactNode;
    /** Names the tree for assistive technologies. */
    label: string;
    /** What to say when there is nothing to show, and why. */
    emptyLabel?: ReactNode;
    className?: string;
}

interface FlatNode {
    node: TreeNode;
    level: number;
    parentId: string | null;
}

/** Visible nodes, top to bottom: what the arrow keys walk through. */
const flatten = (nodes: TreeNode[], expanded: Record<string, boolean>, level = 0, parentId: string | null = null) =>
    nodes.reduce<FlatNode[]>((accumulator, node) => {
        accumulator.push({ node, level, parentId });
        if (node.children?.length && expanded[node.id] !== false) {
            accumulator.push(...flatten(node.children, expanded, level + 1, node.id));
        }
        return accumulator;
    }, []);

/** The node and all of its descendants, in depth order. */
const branchOf = (node: TreeNode): TreeNode[] => [node, ...(node.children ?? []).flatMap(branchOf)];

/**
 * A hierarchy of pickable nodes: the « arborescence de comptes » of the back-office
 * mockup, generalised.
 *
 * **Each node is picked for itself.** The mockup ticks every child along with its
 * parent and shows a parent holding a ticked child as indeterminate; that convention
 * makes « the master account, and it alone » neither expressible nor readable, while
 * the model allows it. Picking a whole branch stays available as an explicit gesture
 * (`onSelectBranch`), which is a choice made on screen rather than one made for the
 * operator.
 *
 * ARIA follows the tree pattern rather than the mockup's markup: a native checkbox
 * inside a `treeitem` gives assistive technologies two checkable things for one row.
 * The state lives on the `treeitem` (`aria-checked`), the box beside the label is
 * decoration, and the row is reached with the arrow keys and toggled with Space.
 */
const Tree = ({
    nodes,
    selected,
    onToggle,
    onSelectBranch,
    branchActionLabel,
    label,
    emptyLabel,
    className,
}: Props) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const itemRefs = useRef(new Map<string, HTMLLIElement>());

    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const visible = useMemo(() => flatten(nodes, expanded), [nodes, expanded]);

    /**
     * Exactly one node is reachable with Tab. The list is rebuilt whenever the tree
     * narrows, so the anchor has to be re-pinned or the whole tree drops out of the
     * tab order.
     */
    const activeId =
        focusedId && visible.some((entry) => entry.node.id === focusedId) ? focusedId : visible[0]?.node.id;

    useEffect(() => {
        if (focusedId && !visible.some((entry) => entry.node.id === focusedId)) {
            setFocusedId(null);
        }
    }, [focusedId, visible]);

    const focusNode = (id: string | undefined) => {
        if (!id) {
            return;
        }
        setFocusedId(id);
        itemRefs.current.get(id)?.focus();
    };

    const toggle = (node: TreeNode) => {
        if (node.disabled || node.selectable === false) {
            return;
        }
        onToggle(node.id, !selectedSet.has(node.id));
    };

    const selectBranch = (node: TreeNode) => {
        const ids = branchOf(node)
            .filter((item) => item.selectable !== false && !item.disabled && !selectedSet.has(item.id))
            .map((item) => item.id);
        if (ids.length) {
            onSelectBranch?.(ids);
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLLIElement>, entry: FlatNode) => {
        const index = visible.findIndex((item) => item.node.id === entry.node.id);
        const hasChildren = Boolean(entry.node.children?.length);
        const isExpanded = expanded[entry.node.id] !== false;

        switch (event.key) {
            case 'ArrowDown':
                focusNode(visible[index + 1]?.node.id);
                break;
            case 'ArrowUp':
                focusNode(visible[index - 1]?.node.id);
                break;
            case 'ArrowRight':
                if (hasChildren && !isExpanded) {
                    setExpanded((current) => ({ ...current, [entry.node.id]: true }));
                } else if (hasChildren) {
                    focusNode(entry.node.children?.[0]?.id);
                }
                break;
            case 'ArrowLeft':
                if (hasChildren && isExpanded) {
                    setExpanded((current) => ({ ...current, [entry.node.id]: false }));
                } else {
                    focusNode(entry.parentId ?? undefined);
                }
                break;
            case 'Home':
                focusNode(visible[0]?.node.id);
                break;
            case 'End':
                focusNode(visible[visible.length - 1]?.node.id);
                break;
            case ' ':
            case 'Enter':
                toggle(entry.node);
                break;
            default:
                return;
        }
        event.preventDefault();
        // A `treeitem` is nested **inside** its parent `treeitem`, so the key would
        // reach the ancestor's handler too and act twice: ArrowDown would move down
        // then back up, ArrowLeft would move to the parent and collapse it on the way.
        event.stopPropagation();
    };

    if (!visible.length) {
        return <p className={clsx('fl-tree__empty', className)}>{emptyLabel}</p>;
    }

    const renderNode = (entry: FlatNode) => {
        const { node, level } = entry;
        const hasChildren = Boolean(node.children?.length);
        const isExpanded = expanded[node.id] !== false;
        const isSelectable = node.selectable !== false;
        const isSelected = isSelectable && selectedSet.has(node.id);
        const reasonId = node.disabledReason ? `fl-tree-reason-${node.id}` : undefined;
        const offersBranch =
            onSelectBranch &&
            hasChildren &&
            branchOf(node).some((item) => item.selectable !== false && !item.disabled && !selectedSet.has(item.id));

        /*
         * `aria-disabled` est toujours émis, jamais omis : l'état est HÉRITÉ par les
         * éléments contenus, et un nœud fils est contenu dans son parent. Omettre
         * l'attribut sur un fils sélectionnable le fait donc annoncer « désactivé »
         * dès que son maître l'est, alors qu'il se coche parfaitement : la souris
         * marchait, le lecteur d'écran mentait. Le `false` explicite coupe l'héritage.
         */
        return (
            <li
                key={node.id}
                role="treeitem"
                aria-level={level + 1}
                aria-checked={isSelectable ? isSelected : undefined}
                aria-disabled={node.disabled === true}
                aria-expanded={hasChildren ? isExpanded : undefined}
                aria-describedby={reasonId}
                tabIndex={node.id === activeId ? 0 : -1}
                ref={(element) => {
                    if (element) {
                        itemRefs.current.set(node.id, element);
                    } else {
                        itemRefs.current.delete(node.id);
                    }
                }}
                onFocus={(event) => event.target === event.currentTarget && setFocusedId(node.id)}
                onKeyDown={(event) => onKeyDown(event, entry)}
                className="fl-tree__item"
            >
                <div
                    className={clsx(
                        'fl-tree__row',
                        isSelected && 'fl-tree__row--selected',
                        node.disabled && 'fl-tree__row--disabled',
                        !isSelectable && 'fl-tree__row--static'
                    )}
                    onClick={() => toggle(node)}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            tabIndex={-1}
                            aria-hidden="true"
                            className="fl-tree__twisty"
                            onClick={(event) => {
                                event.stopPropagation();
                                setExpanded((current) => ({ ...current, [node.id]: !isExpanded }));
                            }}
                        >
                            {isExpanded ? <ChevronDown /> : <ChevronRight />}
                        </button>
                    ) : (
                        <span className="fl-tree__twisty fl-tree__twisty--empty" />
                    )}

                    {/* Decoration: the checked state is carried by the treeitem above. */}
                    {isSelectable && (
                        <span className="fl-tree__check" aria-hidden="true">
                            <Check size={12} />
                        </span>
                    )}

                    <span className="fl-tree__name">{node.label}</span>
                    {node.meta && <span className="fl-tree__meta">{node.meta}</span>}
                    {/* Keyed by position: the qualifiers are a short list rebuilt with
                        the node itself, and carry no identity of their own. */}
                    {node.badges?.map((badge, index) => (
                        <span key={index} className="fl-tree__badge">
                            {badge}
                        </span>
                    ))}

                    {node.disabledReason && (
                        <span id={reasonId} className="fl-tree__reason">
                            {node.disabledReason}
                        </span>
                    )}

                    {offersBranch && (
                        <button
                            type="button"
                            tabIndex={-1}
                            className="fl-tree__branch-action"
                            onClick={(event) => {
                                event.stopPropagation();
                                selectBranch(node);
                            }}
                        >
                            {branchActionLabel}
                        </button>
                    )}
                </div>

                {hasChildren && isExpanded && (
                    <ul role="group" className="fl-tree__group">
                        {(node.children ?? []).map((child) =>
                            renderNode({ node: child, level: level + 1, parentId: node.id })
                        )}
                    </ul>
                )}
            </li>
        );
    };

    return (
        <ul role="tree" aria-multiselectable="true" aria-label={label} className={clsx('fl-tree', className)}>
            {nodes.map((node) => renderNode({ node, level: 0, parentId: null }))}
        </ul>
    );
};

export default Tree;
